/**
 * Image Processing Utilities
 *
 * Core utilities for client-side image processing including:
 * - Dimension calculation with aspect ratio preservation
 * - Crop region computation for center, top, and custom positions
 * - Image compression and format conversion via Canvas API
 * - Image dimension detection
 * - Supabase Storage upload with access token
 *
 * All processed images maintain uniform height while preserving
 * original aspect ratio for width, ensuring consistent layout.
 */
import type {
  CropPosition,
  CropRegion,
  ImageDimensions,
  ImageProcessingOptions,
  ProcessedImage,
} from '@/types/image';

// ============================================================================
// Dimension Calculations
// ============================================================================

/**
 * Calculate scaled dimensions that maintain the original aspect ratio
 * while fitting within the target height constraints.
 *
 * Handles portrait, landscape, and square images uniformly:
 * - Portrait (h > w): Scales to target height, width becomes smaller
 * - Landscape (w > h): Scales to target height, width becomes larger
 * - Square (w == h): Both dimensions equal target height
 */
export function calculateScaledDimensions(
  originalWidth: number,
  originalHeight: number,
  targetHeight: number,
  options: Partial<ImageProcessingOptions> = {}
): { width: number; height: number; scale: number } {
  const {
    minWidth = 100,
    maxWidth = 1200,
    allowUpscale = false,
  } = options;

  // Calculate scale factor to reach target height
  const scale = targetHeight / originalHeight;

  // Calculate new dimensions
  let newWidth = Math.round(originalWidth * scale);
  let newHeight = targetHeight;

  // Prevent upscaling if not allowed
  if (!allowUpscale && scale > 1) {
    return {
      width: originalWidth,
      height: originalHeight,
      scale: 1,
    };
  }

  // Enforce minimum width
  if (newWidth < minWidth) {
    const minScale = minWidth / originalWidth;
    newWidth = minWidth;
    newHeight = Math.round(originalHeight * minScale);
  }

  // Enforce maximum width
  if (newWidth > maxWidth) {
    const maxScale = maxWidth / originalWidth;
    newWidth = maxWidth;
    newHeight = Math.round(originalHeight * maxScale);
  }

  return {
    width: newWidth,
    height: newHeight,
    scale,
  };
}

/**
 * Calculate the crop region based on the position preset.
 *
 * Returns a CropRegion with percentages that define which portion
 * of the original image should be used to fill the target dimensions.
 */
export function calculateCropRegion(
  originalWidth: number,
  originalHeight: number,
  targetWidth: number,
  targetHeight: number,
  position: CropPosition
): CropRegion {
  const originalAspect = originalWidth / originalHeight;
  const targetAspect = targetWidth / targetHeight;

  // If the original aspect ratio matches the target, no cropping needed
  if (Math.abs(originalAspect - targetAspect) < 0.01) {
    return { x: 0, y: 0, width: 100, height: 100 };
  }

  let cropWidth: number;
  let cropHeight: number;
  let cropX: number;
  let cropY: number;

  if (originalAspect > targetAspect) {
    // Image is wider than target - crop horizontally
    cropHeight = 100;
    cropWidth = (targetAspect / originalAspect) * 100;

    switch (position) {
      case 'center':
        cropX = (100 - cropWidth) / 2;
        break;
      case 'top':
        cropX = (100 - cropWidth) / 2; // Center horizontally
        break;
      case 'custom':
        cropX = (100 - cropWidth) / 2; // Default to center
        break;
      default:
        cropX = (100 - cropWidth) / 2;
    }
    cropY = 0;
  } else {
    // Image is taller than target - crop vertically
    cropWidth = 100;
    cropHeight = (originalAspect / targetAspect) * 100;

    switch (position) {
      case 'center':
        cropY = (100 - cropHeight) / 2;
        break;
      case 'top':
        cropY = 0; // Start from top
        break;
      case 'custom':
        cropY = (100 - cropHeight) / 2; // Default to center
        break;
      default:
        cropY = (100 - cropHeight) / 2;
    }
    cropX = 0;
  }

  // Clamp values to valid range
  return {
    x: Math.max(0, Math.min(100, cropX)),
    y: Math.max(0, Math.min(100, cropY)),
    width: Math.max(0, Math.min(100, cropWidth)),
    height: Math.max(0, Math.min(100, cropHeight)),
  };
}

// ============================================================================
// Image Dimension Detection
// ============================================================================

/**
 * Load an image and return its natural dimensions.
 * Handles CORS, timeouts, and errors gracefully.
 */
export function getImageDimensions(
  url: string,
  timeoutMs = 10000
): Promise<ImageDimensions> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';

    const timer = setTimeout(() => {
      reject(new Error(`Image dimension detection timed out after ${timeoutMs}ms`));
    }, timeoutMs);

    img.onload = () => {
      clearTimeout(timer);
      const width = img.naturalWidth;
      const height = img.naturalHeight;
      resolve({
        width,
        height,
        aspectRatio: width / height,
      });
    };

    img.onerror = () => {
      clearTimeout(timer);
      reject(new Error(`Failed to load image for dimension detection: ${url}`));
    };

    img.src = url;
  });
}

/**
 * Get image dimensions from a File object
 */
export function getFileImageDimensions(file: File): Promise<ImageDimensions> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);

    const timer = setTimeout(() => {
      URL.revokeObjectURL(url);
      reject(new Error('Image dimension detection timed out'));
    }, 10000);

    img.onload = () => {
      clearTimeout(timer);
      URL.revokeObjectURL(url);
      resolve({
        width: img.naturalWidth,
        height: img.naturalHeight,
        aspectRatio: img.naturalWidth / img.naturalHeight,
      });
    };

    img.onerror = () => {
      clearTimeout(timer);
      URL.revokeObjectURL(url);
      reject(new Error('Failed to read image dimensions from file'));
    };

    img.src = url;
  });
}

// ============================================================================
// Image Processing & Compression
// ============================================================================

/**
 * Apply a crop region to an image and return the cropped result as a data URL.
 *
 * @param imageUrl - Source image URL
 * @param crop - Crop region in percentages
 * @param outputWidth - Desired output width in pixels
 * @param outputHeight - Desired output height in pixels
 * @returns Promise resolving to cropped image data URL
 */
export async function cropImage(
  imageUrl: string,
  crop: CropRegion,
  outputWidth: number,
  outputHeight: number
): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';

    const timer = setTimeout(() => {
      reject(new Error('Image cropping timed out'));
    }, 15000);

    img.onload = () => {
      clearTimeout(timer);

      // Calculate pixel coordinates from percentages
      const sourceX = (crop.x / 100) * img.naturalWidth;
      const sourceY = (crop.y / 100) * img.naturalHeight;
      const sourceWidth = (crop.width / 100) * img.naturalWidth;
      const sourceHeight = (crop.height / 100) * img.naturalHeight;

      // Create canvas for cropping
      const canvas = document.createElement('canvas');
      canvas.width = outputWidth;
      canvas.height = outputHeight;

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Failed to get canvas context'));
        return;
      }

      // Enable high-quality rendering
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';

      // Draw cropped region
      ctx.drawImage(
        img,
        sourceX,
        sourceY,
        sourceWidth,
        sourceHeight,
        0,
        0,
        outputWidth,
        outputHeight
      );

      // Return as data URL
      resolve(canvas.toDataURL('image/webp', 0.85));
    };

    img.onerror = () => {
      clearTimeout(timer);
      reject(new Error('Failed to load image for cropping'));
    };

    img.src = imageUrl;
  });
}

/**
 * Compress an image file and return a compressed Blob.
 *
 * Uses the Canvas API to resize and compress the image.
 * Supports WebP, JPEG, and PNG output formats.
 *
 * @param file - Source image file
 * @param options - Processing options
 * @returns Promise resolving to compressed blob and metadata
 */
export async function compressImage(
  file: File,
  options: ImageProcessingOptions
): Promise<{ blob: Blob; dimensions: ProcessedImage }> {
  const dimensions = await getFileImageDimensions(file);

  const scaled = calculateScaledDimensions(
    dimensions.width,
    dimensions.height,
    options.targetHeight,
    options
  );

  // Create canvas for compression
  const canvas = document.createElement('canvas');
  canvas.width = scaled.width;
  canvas.height = scaled.height;

  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Failed to get canvas context');
  }

  // Enable high-quality rendering
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';

  // Load and draw image
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(url);

      // Draw with background color if specified (for contain mode)
      if (options.backgroundColor) {
        ctx.fillStyle = options.backgroundColor;
        ctx.fillRect(0, 0, scaled.width, scaled.height);
      }

      ctx.drawImage(img, 0, 0, scaled.width, scaled.height);

      // Determine MIME type
      const mimeType = options.compressFormat === 'webp'
        ? 'image/webp'
        : options.compressFormat === 'png'
          ? 'image/png'
          : 'image/jpeg';

      // Compress
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error('Canvas toBlob failed'));
            return;
          }

          resolve({
            blob,
            dimensions: {
              url: URL.createObjectURL(blob),
              originalWidth: dimensions.width,
              originalHeight: dimensions.height,
              processedWidth: scaled.width,
              processedHeight: scaled.height,
              aspectRatio: scaled.width / scaled.height,
              fileSize: blob.size,
            },
          });
        },
        mimeType,
        options.compressQuality
      );
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load image for compression'));
    };

    img.src = url;
  });
}

/**
 * Process an image with optional cropping and compression.
 *
 * This is the main entry point for image processing. It:
 * 1. Reads the source image dimensions
 * 2. Calculates the crop region based on position
 * 3. Applies the crop if needed
 * 4. Compresses the result
 *
 * @param file - Source image file
 * @param options - Processing options
 * @param cropPosition - Crop position preset (center, top, custom)
 * @param customCrop - Custom crop region (used when cropPosition is 'custom')
 * @returns Promise resolving to processed blob and metadata
 */
export async function processImage(
  file: File,
  options: ImageProcessingOptions,
  cropPosition: CropPosition = 'center',
  customCrop?: CropRegion
): Promise<{ blob: Blob; dimensions: ProcessedImage }> {
  const dimensions = await getFileImageDimensions(file);

  // Calculate target width to maintain aspect ratio at target height
  const scaled = calculateScaledDimensions(
    dimensions.width,
    dimensions.height,
    options.targetHeight,
    options
  );

  // Determine if cropping is needed
  const originalAspect = dimensions.width / dimensions.height;
  const targetAspect = scaled.width / scaled.height;
  const needsCrop = Math.abs(originalAspect - targetAspect) > 0.05;

  if (needsCrop) {
    // Calculate crop region
    const cropRegion = customCrop || calculateCropRegion(
      dimensions.width,
      dimensions.height,
      scaled.width,
      scaled.height,
      cropPosition
    );

    // Create canvas for crop + resize
    const canvas = document.createElement('canvas');
    canvas.width = scaled.width;
    canvas.height = scaled.height;

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('Failed to get canvas context');
    }

    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    // Calculate source pixel coordinates
    const sourceX = (cropRegion.x / 100) * dimensions.width;
    const sourceY = (cropRegion.y / 100) * dimensions.height;
    const sourceWidth = (cropRegion.width / 100) * dimensions.width;
    const sourceHeight = (cropRegion.height / 100) * dimensions.height;

    return new Promise((resolve, reject) => {
      const img = new Image();
      const url = URL.createObjectURL(file);

      img.onload = () => {
        URL.revokeObjectURL(url);

        if (options.backgroundColor) {
          ctx.fillStyle = options.backgroundColor;
          ctx.fillRect(0, 0, scaled.width, scaled.height);
        }

        ctx.drawImage(
          img,
          sourceX,
          sourceY,
          sourceWidth,
          sourceHeight,
          0,
          0,
          scaled.width,
          scaled.height
        );

        const mimeType = options.compressFormat === 'webp'
          ? 'image/webp'
          : options.compressFormat === 'png'
            ? 'image/png'
            : 'image/jpeg';

        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('Canvas toBlob failed'));
              return;
            }

            resolve({
              blob,
              dimensions: {
                url: URL.createObjectURL(blob),
                originalWidth: dimensions.width,
                originalHeight: dimensions.height,
                processedWidth: scaled.width,
                processedHeight: scaled.height,
                aspectRatio: scaled.width / scaled.height,
                cropRegion,
                fileSize: blob.size,
              },
            });
          },
          mimeType,
          options.compressQuality
        );
      };

      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('Failed to load image for processing'));
      };

      img.src = url;
    });
  }

  // No cropping needed - just compress
  return compressImage(file, options);
}

// ============================================================================
// Supabase Storage Upload
// ============================================================================

/**
 * Upload a processed image to Supabase Storage.
 *
 * Uses the Supabase access token for authenticated uploads.
 * Images are stored in the 'product_images' bucket with a clean,
 * organized path structure.
 *
 * @param blob - Image blob to upload
 * @param fileName - Desired file name (auto-generated if not provided)
 * @param path - Subdirectory path within the bucket
 * @returns Promise resolving to the public URL of the uploaded image
 */
export async function uploadToSupabaseStorage(
  blob: Blob,
  fileName?: string,
  path: string = 'products'
): Promise<{ publicUrl: string; filePath: string }> {
  const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
  const SUPABASE_ACCESS_TOKEN = import.meta.env.SUPABASE_ACCESS_TOKEN;

  if (!SUPABASE_URL || !SUPABASE_ACCESS_TOKEN) {
    throw new Error(
      'Supabase credentials not configured. Ensure VITE_SUPABASE_URL and SUPABASE_ACCESS_TOKEN are set in .env'
    );
  }

  // Generate unique filename if not provided
  const timestamp = Date.now();
  const randomStr = Math.random().toString(36).substring(2, 9);
  const ext = blob.type.includes('webp') ? 'webp' : blob.type.includes('png') ? 'png' : 'jpg';
  const finalFileName = fileName || `product-${timestamp}-${randomStr}.${ext}`;
  const filePath = `${path}/${finalFileName}`;

  // Upload using Supabase REST API with access token
  const response = await fetch(
    `${SUPABASE_URL}/storage/v1/object/product_images/${filePath}`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${SUPABASE_ACCESS_TOKEN}`,
        'Content-Type': blob.type || 'image/jpeg',
        'x-upsert': 'true',
      },
      body: blob,
    }
  );

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      `Upload failed with status ${response.status}: ${errorText}`
    );
  }

  // Construct public URL
  const publicUrl = `${SUPABASE_URL}/storage/v1/object/public/product_images/${filePath}`;

  return { publicUrl, filePath };
}

/**
 * Upload an image file directly to Supabase Storage with processing.
 *
 * This is the main upload function that:
 * 1. Processes the image (resize, compress, crop)
 * 2. Uploads to Supabase Storage
 * 3. Returns the public URL and processing metadata
 *
 * @param file - Source image file from user
 * @param options - Processing options
 * @param cropPosition - Crop position preset
 * @param customCrop - Custom crop region
 * @returns Promise resolving to upload result
 */
export async function uploadProductImage(
  file: File,
  options: ImageProcessingOptions,
  cropPosition: CropPosition = 'center',
  customCrop?: CropRegion
): Promise<{
  publicUrl: string;
  filePath: string;
  dimensions: ProcessedImage;
}> {
  // Process the image (crop + compress)
  const { blob, dimensions } = await processImage(
    file,
    options,
    cropPosition,
    customCrop
  );

  // Upload to Supabase Storage
  const { publicUrl, filePath } = await uploadToSupabaseStorage(blob);

  return { publicUrl, filePath, dimensions };
}

/**
 * Delete an image from Supabase Storage.
 *
 * @param filePath - The storage path of the image to delete
 * @returns Promise resolving when deletion is complete
 */
export async function deleteProductImage(filePath: string): Promise<void> {
  const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
  const SUPABASE_ACCESS_TOKEN = import.meta.env.SUPABASE_ACCESS_TOKEN;

  if (!SUPABASE_URL || !SUPABASE_ACCESS_TOKEN) {
    throw new Error('Supabase credentials not configured');
  }

  const response = await fetch(
    `${SUPABASE_URL}/storage/v1/object/product_images/${filePath}`,
    {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${SUPABASE_ACCESS_TOKEN}`,
      },
    }
  );

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      `Delete failed with status ${response.status}: ${errorText}`
    );
  }
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Check if the browser supports WebP format
 */
export function supportsWebP(): boolean {
  const canvas = document.createElement('canvas');
  canvas.width = 1;
  canvas.height = 1;
  return canvas.toDataURL('image/webp').startsWith('data:image/webp');
}

/**
 * Get the best compression format for the current browser
 */
export function getBestCompressionFormat(): 'webp' | 'jpeg' {
  return supportsWebP() ? 'webp' : 'jpeg';
}

/**
 * Format file size for display
 */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

/**
 * Validate that a file is an acceptable image
 */
export function isValidImageFile(file: File): { valid: boolean; error?: string } {
  const maxSize = 10 * 1024 * 1024; // 10MB
  const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];

  if (!file.type.startsWith('image/')) {
    return { valid: false, error: 'File must be an image' };
  }

  if (!allowedTypes.includes(file.type)) {
    return { valid: false, error: 'Unsupported image format. Use JPEG, PNG, WebP, or GIF' };
  }

  if (file.size > maxSize) {
    return { valid: false, error: `File too large. Maximum size is ${formatFileSize(maxSize)}` };
  }

  return { valid: true };
}

/**
 * Generate a unique filename for an uploaded image
 */
export function generateImageFileName(originalName: string): string {
  const timestamp = Date.now();
  const randomStr = Math.random().toString(36).substring(2, 9);
  const ext = originalName.split('.').pop() || 'jpg';
  return `product-${timestamp}-${randomStr}.${ext}`;
}

// ============================================================================
// Batch Image Reprocessing (for existing images)
// ============================================================================

export interface ReprocessResult {
  originalUrl: string;
  newUrl: string;
  success: boolean;
  error?: string;
  originalSize?: number;
  newSize?: number;
}

/**
 * Download an image from a URL and return it as a Blob
 */
async function downloadImage(url: string): Promise<Blob> {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to download image: ${response.status}`);
  }
  return response.blob();
}

/**
 * Reprocess a single image URL: download, process, re-upload
 */
export async function reprocessImageUrl(
  imageUrl: string,
  options: ImageProcessingOptions = GRID_IMAGE_CONFIG,
  cropPosition: CropPosition = 'center'
): Promise<ReprocessResult> {
  try {
    // Download original
    const originalBlob = await downloadImage(imageUrl);
    const originalSize = originalBlob.size;

    // Create file from blob
    const file = new File([originalBlob], 'original-image.jpg', { type: originalBlob.type });

    // Process the image
    const { blob: processedBlob } = await processImage(file, options, cropPosition);

    // Upload processed image
    const { publicUrl } = await uploadToSupabaseStorage(processedBlob);

    return {
      originalUrl: imageUrl,
      newUrl: publicUrl,
      success: true,
      originalSize,
      newSize: processedBlob.size,
    };
  } catch (error) {
    return {
      originalUrl: imageUrl,
      newUrl: imageUrl,
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

/**
 * Batch reprocess multiple image URLs
 * 
 * @param imageUrls - Array of image URLs to reprocess
 * @param options - Processing options (defaults to GRID_IMAGE_CONFIG)
 * @param cropPosition - Crop position preset
 * @param onProgress - Optional callback for progress updates
 * @returns Array of reprocessing results
 * 
 * @example
 * ```ts
 * const results = await batchReprocessImages([
 *   'https://supabase.co/storage/.../image1.jpg',
 *   'https://supabase.co/storage/.../image2.jpg',
 * ], GRID_IMAGE_CONFIG, 'center', (progress) => {
 *   console.log(`Processed ${progress.current}/${progress.total}`);
 * });
 * ```
 */
export async function batchReprocessImages(
  imageUrls: string[],
  options: ImageProcessingOptions = GRID_IMAGE_CONFIG,
  cropPosition: CropPosition = 'center',
  onProgress?: (progress: { current: number; total: number; result: ReprocessResult }) => void
): Promise<ReprocessResult[]> {
  const results: ReprocessResult[] = [];

  for (let i = 0; i < imageUrls.length; i++) {
    const imageUrl = imageUrls[i];
    
    // Skip if already processed (same Supabase URL format)
    if (imageUrl.includes('.webp')) {
      results.push({
        originalUrl: imageUrl,
        newUrl: imageUrl,
        success: true,
      });
      onProgress?.({ current: i + 1, total: imageUrls.length, result: results[results.length - 1] });
      continue;
    }

    const result = await reprocessImageUrl(imageUrl, options, cropPosition);
    results.push(result);
    onProgress?.({ current: i + 1, total: imageUrls.length, result });

    // Small delay to avoid rate limiting
    if (i < imageUrls.length - 1) {
      await new Promise(resolve => setTimeout(resolve, 500));
    }
  }

  return results;
}

/**
 * Get all product image URLs from Supabase database
 * Queries the 'image' field (singular) as that's where product images are stored
 */
export async function getAllProductImageUrls(supabase: any): Promise<{ id: string; url: string }[]> {
  const { data, error } = await supabase
    .from('products')
    .select('id, image')
    .not('image', 'is', null)
    .not('image', 'eq', '');

  if (error) {
    throw new Error(`Failed to fetch products: ${error.message}`);
  }

  const results: { id: string; url: string }[] = [];
  for (const product of data || []) {
    if (product.image && typeof product.image === 'string') {
      results.push({ id: product.id, url: product.image });
    }
  }

  return results;
}

/**
 * Update product image URL in database after reprocessing
 * Updates the 'image' field (singular)
 */
export async function updateProductImage(
  supabase: any,
  productId: string,
  oldUrl: string,
  newUrl: string
): Promise<void> {
  // Update the image field for the product
  const { error: updateError } = await supabase
    .from('products')
    .update({ image: newUrl })
    .eq('id', productId)
    .eq('image', oldUrl);

  if (updateError) {
    throw new Error(`Failed to update product: ${updateError.message}`);
  }
}
